import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { PageContainer } from '@/components/page/PageContainer';
import { PageHeader } from '@/components/page/PageHeader';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { breadcrumbName } from '@/helpers/Utility';
import { ReadEmployee, useGetStaffByIdQuery } from '@/services/gql/graphql.generated';
import strings from '../../team.translations';
import { FC, Suspense } from 'react';
import { FiEdit } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { SkillsEditForm } from './SkillsEditForm';
import { useEmployeesSkillsList } from '@/pages/roster/hooks/useEmployeeSkillsList';

interface TeamMemberSkillsEditProps {}

export const TeamMemberSkillsEdit: FC<TeamMemberSkillsEditProps> = () => {
  const { userId } = useParams<'userId'>();

  const [{ data: userData }] = useGetStaffByIdQuery({
    variables: { id: userId }
  });

  const employee = userData?.employee;

  if (!employee) {
    throw Error('Could not find employee data.');
  }

  const employeeSkillsObj = useEmployeesSkillsList([employee.identityId]);
  const skills = employeeSkillsObj[0].skills;
  const skillIds: string[] = skills.filter(s => !!s.skillId).map(s => s.skillId!);

  const breadcrumbs =  [
        {
          name: strings.teamPageTitle,
          to: '/team'
        },
        {
          name: breadcrumbName(employee as ReadEmployee, strings.employeeName),
          to: `/team-member/${userId}`
        },
        {
          name: strings.editSkills.breadcrumb,
          to: `/team-member/${userId}/set-skills`
        }
      ];

  return (
    <PageContainer>
      <PageHeader
        icon={<FiEdit />}
        pageTitle={strings.editSkills.title + ': ' + `${employee?.firstName ?? ''} ${employee?.lastName ?? ''}`}
        breadcrumbs={breadcrumbs}
      />
      <PageSizer mode={SizerMode.PAGE}>
        <div className="py-5">
          <SubmissionErrorBoundary>
            {/* This local Suspense wrapper handles everything nicely if the data isn't 
              fully cached yet on entry, showing the loader seamlessly!
            */}
            <Suspense fallback={<TableLoader />}>
              <SkillsEditForm
                userId={userId!}
                initSkillIds={skillIds}
              />
            </Suspense>
          </SubmissionErrorBoundary>
        </div>
      </PageSizer>
    </PageContainer>
  );
};
